import { useEffect, useState } from 'react'

import { useFeatureFlags } from '@/hooks/useFeatureFlags'

import { Text } from '@chakra-ui/react'
import { Box } from '@chakra-ui/react'
import { AnimatePresence } from 'framer-motion'

import { AudioPlayer } from '@/components/ui'
import { IncidentQuery } from '@/graphql/generated/operations'
import { mixpanel } from '@/utils/analytics'

import { MixpanelDataIProps } from '../../types/types'
import { getActivityTimeline } from '../utils/utils'
import { FeedItem } from './FeedItem'
import { FeedItemsSkeleton } from './FeedItemsSkeleton'
import { ActionHeader, ActionIcon } from './IncidentAction'
import { IncidentDetails } from './IncidentDetails'
import { IncidentNoteMedia } from './IncidentNoteMedia'
import { ShowMore } from './ShowMore'

interface FeedIProps {
  incident?: IncidentQuery['incident']
  mixpanelData?: MixpanelDataIProps
}

export const Feed = ({ incident, mixpanelData }: FeedIProps) => {
  const [items, setItems] = useState(null)
  const { attachMediaToIncidentNote: isAttachMediaToIncidentNoteEnabled } =
    useFeatureFlags()

  useEffect(() => {
    const actions = incident?.actions
    const events = incident?.events
    const audioMessages = incident?.audioMessages?.edges?.map((a) => a?.node)
    const notes = incident?.notes.edges?.map((a) => a?.node)

    if (actions && audioMessages && notes && events) {
      const activityTimeLine = getActivityTimeline(
        actions,
        events,
        audioMessages,
        notes
      )
      setItems(activityTimeLine)
    }
  }, [incident])

  const trackExpandDescriptionClick = (eventType: 'more' | 'less'): void => {
    let eventName: string
    switch (eventType) {
      case 'more':
        eventName = 'Show More Button Pressed (Feed)'
        break
      case 'less':
        eventName = 'Show Less Button Pressed (Feed)'
        break
    }
    mixpanel.track(eventName, mixpanelData)
  }

  return (
    <Box align='flex-start' p='24px'>
      {!items ? (
        <FeedItemsSkeleton />
      ) : (
        <AnimatePresence>
          <IncidentDetails incident={incident} mixpanelData={mixpanelData} />

          {items?.length > 0 && (
            <Text color='black' fontSize='16px' fontWeight={600} mb='10px'>
              Timeline Feed
            </Text>
          )}

          {items.map((a, i: number) => (
            <FeedItem
              actionType={a?.type || a?.nonActionType}
              avatar={a?.creator?.avatar?.url}
              avatarName={`${a?.creator?.firstName ?? ''} ${
                a?.creator?.lastName ?? ''
              }`.trim()}
              createdAt={a?.createdAt}
              heading={ActionHeader({
                actionType: a?.type || a?.nonActionType,
                username: `${a?.creator?.firstName} ${a?.creator?.lastName}`,
                roleName: a?.creator?.roleName,
                systemMessage: a?.systemMessage,
                technicianName: `${a?.deviceRepairOrder?.technician?.firstName} ${a?.deviceRepairOrder?.technician?.lastName}`,
              })}
              icon={ActionIcon(a?.type || a?.nonActionType)}
              index={i}
              key={i}
            >
              {a?.audioClip?.url && <AudioPlayer url={a?.audioClip?.url} />}
              {a?.message && (
                <ShowMore
                  handleShowLess={() => {
                    trackExpandDescriptionClick('less')
                  }}
                  handleShowMore={() => {
                    trackExpandDescriptionClick('more')
                  }}
                  message={a?.message}
                />
              )}
              {isAttachMediaToIncidentNoteEnabled && a?.media?.length > 0 && (
                <IncidentNoteMedia files={a?.media} />
              )}
            </FeedItem>
          ))}
        </AnimatePresence>
      )}
    </Box>
  )
}
